import { useState } from 'react';
import { ChevronUp, ChevronDown } from 'lucide-react';

/**
 * PropertyNumberField - Reusable number input field with increment/decrement buttons
 *
 * @param {string} label - Field label
 * @param {number} value - Current value
 * @param {function} onChange - Callback(newValue) when value changes
 * @param {string} unit - Optional unit to display (e.g., 'cm', 'm', '°')
 * @param {number} min - Minimum value
 * @param {number} max - Maximum value
 * @param {number} step - Step increment
 * @param {boolean} disabled - Whether field is disabled
 * @param {boolean} commitOnBlur - When true, typing edits a local draft and the
 *   parent onChange only fires on blur / Enter (not per keystroke). This lets a
 *   user type a multi-digit value into a clamped field ([min,max]) without each
 *   intermediate keystroke being clamped/reformatted mid-typing. The +/- buttons
 *   still commit immediately. Default false keeps the original behavior.
 */
const PropertyNumberField = ({
  label,
  value,
  onChange,
  unit = '',
  min,
  max,
  step = 1,
  disabled = false,
  commitOnBlur = false,
}) => {
  // While editing in commitOnBlur mode, `draft` holds the raw input string;
  // null means "not editing — mirror the committed value".
  const [draft, setDraft] = useState(null);
  const editing = commitOnBlur && draft !== null;

  const currentNumeric = editing ? parseFloat(draft) || 0 : value || 0;

  const commitValue = (newValue) => {
    if (editing) setDraft(null);
    onChange(newValue);
  };

  const handleIncrement = () => {
    const newValue = currentNumeric + step;
    if (max === undefined || newValue <= max) {
      commitValue(newValue);
    }
  };

  const handleDecrement = () => {
    const newValue = currentNumeric - step;
    if (min === undefined || newValue >= min) {
      commitValue(newValue);
    }
  };

  const handleInputChange = (e) => {
    if (commitOnBlur) {
      setDraft(e.target.value);
    } else {
      onChange(parseFloat(e.target.value) || 0);
    }
  };

  const handleFocus = () => {
    if (commitOnBlur) {
      setDraft(value === undefined || value === null ? '' : String(value));
    }
  };

  const handleBlur = () => {
    if (commitOnBlur && draft !== null) {
      const parsed = parseFloat(draft);
      onChange(Number.isNaN(parsed) ? 0 : parsed);
      setDraft(null);
    }
  };

  const handleKeyDown = (e) => {
    if (commitOnBlur && e.key === 'Enter') {
      e.currentTarget.blur();
    }
  };

  const inputValue = editing ? draft : value || '';

  return (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {label}
      </label>
      <div className="flex items-center">
        <div className="flex items-center flex-1">
          <input
            type="number"
            value={inputValue}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onKeyDown={handleKeyDown}
            min={min}
            max={max}
            step={step}
            disabled={disabled}
            className="w-full px-3 py-2 text-sm border border-gray-300 rounded-l
              focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500
              disabled:bg-gray-100 disabled:cursor-not-allowed
              [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
          />
          <div className="flex flex-col border-y border-r border-gray-300 rounded-r">
            <button
              onClick={handleIncrement}
              disabled={disabled || (max !== undefined && currentNumeric >= max)}
              className="px-1 py-1 hover:bg-gray-100 active:bg-gray-200 transition-colors
                disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={`${label} erhöhen`}
              type="button"
            >
              <ChevronUp size={14} className="text-gray-600" />
            </button>
            <button
              onClick={handleDecrement}
              disabled={disabled || (min !== undefined && currentNumeric <= min)}
              className="px-1 py-1 hover:bg-gray-100 active:bg-gray-200 transition-colors border-t border-gray-300
                disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label={`${label} verringern`}
              type="button"
            >
              <ChevronDown size={14} className="text-gray-600" />
            </button>
          </div>
        </div>
        {unit && (
          <span className="text-xs text-gray-600 ml-2 whitespace-nowrap">{unit}</span>
        )}
      </div>
    </div>
  );
};

export default PropertyNumberField;
